import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";
import { useAuth } from "./useAuth";

/**
 * @typedef {object} DriverPagination
 * @property {number} page
 * @property {number} rowsPerPage
 */

/**
 * @typedef {object} DriverFilters
 * @property {string} search
 */

/**
 * Initializes state from localStorage.
 * @returns {{filters: DriverFilters, pagination: DriverPagination}}
 */


// Auxiliary function to initialize filters from localStorage, outside the hook.
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("driversFilter");
  const defaults = {
    filters: {
      search: "",
    },
    pagination: {
      page: 1,
      rowsPerPage: 20,
    },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    return {
      filters: {
        search: savedState.search || defaults.filters.search,
      },
      pagination: {
        page: savedState.page || defaults.pagination.page,
        rowsPerPage: savedState.rowsPerPage || defaults.pagination.rowsPerPage,
      },
    };
  }
  return defaults;
};

/**
 * Custom Hook to fetch and manage a paginated/searchable list of drivers.
 *
 * @returns {{
 * drivers: Array<object>,
 * loading: boolean,
 * error: string | null,
 * auxSearcher: string,
 * pagination: DriverPagination,
 * filters: DriverFilters,
 * driversCount: number,
 * setAuxSearcher: React.Dispatch<React.SetStateAction<string>>,
 * handleFilterChange: (name: string, value: any) => void,
 * handleChangePage: (newPage: number) => void,
 * handleChangeRowsPerPage: (newRows: number) => void,
 * refetchDrivers: () => Promise<void>
 * }}
 */

export function useDrivers() {
  // Data status and IU
  const [initialState] = useState(() => getInitialState());
  const [filters, setFilters] = useState(initialState.filters);
  const [pagination, setPagination] = useState(initialState.pagination);
  const [auxSearcher, setAuxSearcher] = useState(filters.search);
  const [drivers, setDrivers] = useState([]);
  const [driversCount, setDriversCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useAuth();

  // STATE MANNERS (HANDLERS)
  const handleFilterChange = useCallback((filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const fetchDrivers = useCallback(async () => {
    if (user.admin_plan?.plan_type !== "admin_checkout") {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const params = {
      search: filters.search || undefined,
      page: pagination.page,
      per: pagination.rowsPerPage,
    }
    try {
      const {
        data: { drivers, page, total_count: totalCount, num_pages: numPages },
      } = await axios.get("drivers/get-all", { params });
      setDrivers(drivers || []);
      setDriversCount(totalCount || 0);

    } catch (error) {
      console.error(`No se pudo obtener los conductores: ${error}`);
      setError("No se pudo obtener los conductores");
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    pagination,
  ]);

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (auxSearcher !== filters.search) {
        handleFilterChange("search", auxSearcher);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [auxSearcher, filters.search, handleFilterChange]);

  useEffect(() => {
    localStorage.setItem(
      "driversFilter",
      JSON.stringify({
        ...filters,
        ...pagination,
      })
    );
  }, [filters, pagination]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    drivers,
    loading,
    error,
    auxSearcher,
    pagination,
    filters,
    driversCount,

    // MANNERS (Setters y Actions)
    setDriversCount,
    setAuxSearcher,
    handleFilterChange,
    handleChangePage,
    handleChangeRowsPerPage,
    refetchDrivers: fetchDrivers
  };
}
